/**
 * Formats any user input into "+1 (XXX) XXX-XXXX" as they type.
 * - Forces country code 1
 * - Accepts only digits
 * - Gracefully handles partial input
 */
export function formatUSPhonePretty(input: string): string {
  // Keep only digits
  const digits = input.replace(/\D/g, '');

  // Ensure we have a leading 1 (US country code)
  const withCountry = digits.startsWith('1') ? digits : `1${digits}`;

  // Max 11 digits total: 1 + 10 (area+local)
  const d = withCountry.slice(0, 11);

  // d[0] is country (1)
  const area = d.slice(1, 4);
  const prefix = d.slice(4, 7);
  const line = d.slice(7, 11);

  // Build progressively so it looks nice while typing
  if (d.length <= 1) return '+1';
  if (d.length <= 4) return `+1 (${area}`;
  if (d.length <= 7) return `+1 (${area}) ${prefix}`;
  return `+1 (${area}) ${prefix}-${line}`;
}

/**
 * Parses any phone-ish string to E.164 for US: "+1XXXXXXXXXX".
 * Returns null if there aren’t 10 national digits.
 */
export function parseUSPhoneToE164(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  // Strip a leading 1 if present, then expect exactly 10 digits for US national number
  const national = digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return null;
  return `+1${national}`;
}

/**
 * The shape a phone belongs in while it is sitting in a field the customer reads — the same
 * "+1 (XXX) XXX-XXXX" both signup steps produce as you type, so a number we filled in for them
 * looks like a number they typed.
 *
 * Anything that isn't a dialable US number is handed back untouched, empty strings included:
 * `formatUSPhonePretty('')` returns a bare "+1", which in an empty field is content we invented.
 */
export function formatUSPhoneForDisplay(input: string): string {
  const e164 = parseUSPhoneToE164(input);
  return e164 ? formatUSPhonePretty(e164) : input;
}

/**
 * Validates if a string is a valid US E.164: "+1" followed by 10 digits.
 */
export function isValidUSE164(phone: string): boolean {
  return /^\+1\d{10}$/.test(phone);
}
